import type { ObservableMap } from 'mobx';

import type { FormStore } from 'src/features/well-form/entities/form.entity';
import type { GeoLoadRigOperation } from 'src/features/well-form/entities/geological-load/geo-load-rig-operation.entity';
import type { TGeologicalLoadKeys } from 'src/features/well-form/entities/geological-load/geological-load-service';
import { GOPLAN_GEOLOGICAL_TASK_PREFIX } from 'src/features/well-form/entities/geological-load/geological-load-service';
import type { GeologicalLoad } from 'src/features/well-form/entities/geological-load/geological-load.entity';
import {
  RIG_OPERATION_FIELD_PREFIX,
  RIG_OPERATION_ID_KEY,
  RIG_OPERATION_INDEX_KEY,
  RIG_OPERATION_STAGES_KEY,
  RIG_OPERATIONS_KEY,
} from 'src/features/well-form/entities/rig-operations/consts';
import type { RigOperationSection } from 'src/features/well-form/entities/rig-operations/rig-operation-section.entity';
import type { RigOperationStage } from 'src/features/well-form/entities/rig-operations/rig-operation-stage.entity';
import type { RigOperation } from 'src/features/well-form/entities/rig-operations/rig-operation.entity';
import {
  isRigOperationField,
  isSectionField,
  isStageField,
} from 'src/features/well-form/entities/rig-operations/rig-operations-service';
import type { Item } from 'src/shared/entities/abstract-control-entities';
import { DateIntervalField, PassingIntervalField } from 'src/shared/entities/control-entities';
import { hasValue } from 'src/shared/utils/common';
import { getInitialTripleIdsByWell } from 'src/shared/utils/get-triple-ids';
import { isNumberArray } from 'src/shared/utils/is-number-array';
import { isStringArray } from 'src/shared/utils/is-string-array';
import { isStringNumberOrBoolean } from 'src/shared/utils/is-string-number-or-boolean';

import type {
  TSerializedForm,
  TSerializedRigOperation,
  TSerializedStage,
  TSerializedSection,
  TIdTriple,
} from '../types';

type TSerializedFields = Record<string, string | number | boolean | string[] | number[] | null>;

const serializeFields = (items: Item[], byFieldId?: boolean): TSerializedFields => {
  const serializedFields: TSerializedFields = {};
  for (const item of items) {
    if (item.formElementRefId) {
      if (
        isStringNumberOrBoolean(item.value) ||
        !hasValue(item.value) ||
        isStringArray(item.value) ||
        isNumberArray(item.value)
      ) {
        if (byFieldId) {
          serializedFields[item.fieldId] = hasValue(item.value) ? item.value : null;
          continue;
        }
        if (!['Common_Well.wellNumber', 'GOplan_RigOperation.passingValue'].includes(item.formElementRefId)) {
          serializedFields[item.formElementRefId] = hasValue(item.value) ? item.value : null;
        }
      }
    }
    if (item instanceof DateIntervalField) {
      if (item.startDate) {
        serializedFields['startDate'] = item.startDate.unix();
      }
      if (item.endDate) {
        serializedFields['endDate'] = item.endDate.unix();
      }
      if (item.duration !== null) {
        serializedFields['duration'] = item.duration.valueOf();
      }
    }

    if (item instanceof PassingIntervalField) {
      if (item.passingValueMin !== null) {
        serializedFields['passingValueMin'] = item.passingValueMin;
      }
      if (item.passingValueMax !== null) {
        serializedFields['passingValueMax'] = item.passingValueMax;
      }
      if (item.passingValue !== null) {
        serializedFields['passingValue'] = item.passingValue;
      }
    }
  }

  return serializedFields;
};

export const serializeRigOperation = (rigOperation: RigOperation): TSerializedRigOperation => {
  const serializedRigOperation: TSerializedRigOperation = {
    [RIG_OPERATION_STAGES_KEY]: [],
  };

  serializedRigOperation[RIG_OPERATION_INDEX_KEY] = rigOperation.index;

  if (rigOperation.rigOperationId) {
    serializedRigOperation[RIG_OPERATION_ID_KEY] = rigOperation.rigOperationId;
  }

  for (const fieldKey in rigOperation.data) {
    if (!isRigOperationField(fieldKey)) {
      continue;
    }

    if (fieldKey === 'index') {
      continue;
    }

    const key = `${RIG_OPERATION_FIELD_PREFIX}.${fieldKey}`;
    const value = rigOperation.data[fieldKey];

    if (hasValue(value)) {
      serializedRigOperation[key] = value;
    }
  }

  const serializedStages: TSerializedStage[] = [];

  rigOperation.stages.forEach((stage, index) => {
    serializedStages.push(serializeStage(stage, index));
  });

  serializedRigOperation[RIG_OPERATION_STAGES_KEY] = serializedStages;

  return serializedRigOperation;
};

export const serializeStage = (stage: RigOperationStage, index?: number): TSerializedStage => {
  const serializedStage: Partial<TSerializedStage> = {};

  if (hasValue(index)) {
    serializedStage.index = index;
  }

  for (const fieldKey in stage.data) {
    if (!isStageField(fieldKey)) {
      continue;
    }

    if (fieldKey === 'index') {
      continue;
    }

    const value = stage.data[fieldKey];

    if (hasValue(value)) {
      serializedStage[fieldKey] = value;
    }
  }

  serializedStage.sections = stage.sections.map((section) => serializeSection(section));

  return serializedStage as TSerializedStage;
};

export const serializeSection = (section: RigOperationSection): TSerializedSection => {
  const serializedSection: Partial<TSerializedSection> = {};

  for (const fieldKey in section.data) {
    if (!isSectionField(fieldKey)) {
      continue;
    }

    if (fieldKey === 'index') {
      continue;
    }

    const value = section.data[fieldKey];

    if (hasValue(value)) {
      serializedSection[fieldKey] = value;
    }
  }

  return serializedSection;
};

// TODO: изменить тип сериализованных данных на unknown
const serializeGeologicalLoad = (
  geoLoad: GeologicalLoad | null,
  prefix?: string,
): Record<string, boolean | number | number[] | null> => {
  if (!geoLoad) {
    return {};
  }

  const geoLoadData = geoLoad.data;
  const serializedData: Record<string, boolean | number | number[] | null> = {};

  for (const rawKey in geoLoadData) {
    const key = prefix ? `${prefix}.${rawKey}` : rawKey;
    const value = geoLoadData[rawKey as TGeologicalLoadKeys];

    if (hasValue(value)) {
      serializedData[key] = value;
    }
  }

  return serializedData;
};

export const serializeRigOperations = (
  rigOperations: RigOperation[],
  geoLoadRigOperationsByRigOperationIdMap: ObservableMap<number, GeoLoadRigOperation>,
): TSerializedRigOperation[] => {
  const serializedRigOperations: TSerializedRigOperation[] = [];

  rigOperations.forEach((rigOperation) => {
    const geoLoadRigOperation = geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);
    const serializedRigOperation = {
      ...serializeRigOperation(rigOperation),
      ...serializeGeologicalLoad(geoLoadRigOperation?.geologicalLoad ?? null),
    };

    serializedRigOperation[RIG_OPERATION_STAGES_KEY] = rigOperation.stages.map((stage, index): TSerializedStage => {
      const geoLoadStage = geoLoadRigOperation?.stages.find(
        (geoLoadStage) => geoLoadStage.stageEntityId === stage.entityId,
      );
      const serializedStage = {
        ...serializeStage(stage, index),
        ...serializeGeologicalLoad(geoLoadStage?.geologicalLoad ?? null),
      };

      serializedStage.sections = stage.sections.map((section): TSerializedSection => {
        const geoLoadSection = geoLoadStage?.sections.find(
          (geoLoadSection) => geoLoadSection.sectionEntityId === section.entityId,
        );
        const serializedSection = {
          ...serializeSection(section),
          ...serializeGeologicalLoad(geoLoadSection?.geologicalLoad ?? null),
        };

        return serializedSection;
      });

      return serializedStage;
    });

    serializedRigOperations.push(serializedRigOperation);
  });

  return serializedRigOperations;
};

export const serializeFormForRequest = (formStore: FormStore, tripleIds?: TIdTriple[] | null): TSerializedForm => {
  let serializedForm: TSerializedForm = {
    'GOplan_GeologicalTask.index': formStore.index,
  };
  const newTripleIds =
    tripleIds ?? (formStore.initialFormData ? getInitialTripleIdsByWell(formStore.initialFormData) : null);

  newTripleIds?.forEach((triple) => {
    if (triple.objectType !== 'GOplan_RigOperation' && triple.objectType !== 'GOplan_PlanWellTriple') {
      serializedForm[`${triple.objectType}.id`] = triple.id;
    }
  });

  const { mainDataTab, rigOperationsService, geologicalLoadService } = formStore;

  for (const column of mainDataTab.columns) {
    serializedForm = { ...serializedForm, ...serializeFields(column.items) };
  }
  // TODO: отрефакторить во время рефакторинга вкладки этапов работ
  const serializedRigOperations = serializeRigOperations(
    rigOperationsService.rigOperations,
    geologicalLoadService.geoLoadRigOperationsByRigOperationIdMap,
  );

  // Временное решение
  const isSectionDetailing = (() => {
    return (
      rigOperationsService.rigOperations.length > 1 ||
      (!!rigOperationsService.rigOperations.length &&
        rigOperationsService.rigOperations[0].stages.some((s) => s.sections.length))
    );
  })();

  serializedForm['GOplan_GeologicalTask.sectionsDetailing'] = isSectionDetailing;

  serializedForm[RIG_OPERATIONS_KEY] = serializedRigOperations;

  if (!serializedRigOperations.length) {
    serializedForm = {
      ...serializedForm,
      ...serializeGeologicalLoad(geologicalLoadService.geotaskGeoLoad, GOPLAN_GEOLOGICAL_TASK_PREFIX),
    };
  }

  return serializedForm;
};
